import { Map, Record, List } from "immutable";
import { AnyAction } from "redux";

import { LOADING_STATE } from "@typings/reduxThunkActions";

import {
  ACTION_FAIL,
  ACTION_PENDING,
  ACTION_SUCCESS,
} from "@services/AsyncActionCreatorFactory";

import {
  AGENT_COMPENSATION_TYPE,
  Holiday as HolidayInterface,
  HolidayReducer,
} from "./holidays.typings";
import {
  DELETE_HOLIDAY,
  FETCH_HOLIDAYS,
  INIT_PROCESSED_HOLIDAY,
  UPDATE_PROCESSED_HOLIDAY,
  CLEAR_PROCESSED_HOLIDAY,
  FETCH_CONTRACTS_TO_PAUSE,
} from "./holidays.actions";

export const initialHoliday = Record<HolidayInterface>({
  id: "",
  startDate: "",
  endDate: "",
  skipCleanings: true,
  compensateAgent: false,
  agentCompensationAmount: 0,
  agentCompensationType: AGENT_COMPENSATION_TYPE.FULL,
  visible: true,
  calendarStartDate: "",
  pauseCleanings: false,
  actions: [],
});

const initialState = Record({
  holidays: Map<string, Record<HolidayInterface>>(),
  processedHoliday: null,
  loadingState: LOADING_STATE.IDLE,
  contractsToPause: List(),
});

const holidaysReducer = (
  store: HolidayReducer = initialState(),
  action: AnyAction,
): HolidayReducer => {
  const { type, payload } = action;

  switch (type) {
    case `${FETCH_HOLIDAYS}${ACTION_PENDING}`: {
      return store.set("loadingState", LOADING_STATE.LOADING);
    }

    case `${FETCH_HOLIDAYS}${ACTION_SUCCESS}`: {
      const {
        data: { holidays },
      } = payload;

      const holidaysMap = holidays.reduce(
        (
          acc: Map<string, Record<HolidayInterface>>,
          holiday: HolidayInterface,
        ) =>
          acc.set(
            holiday.id,
            initialHoliday({
              ...holiday,
              startDate: holiday.startDate.replace(" ", "T"),
              endDate: holiday.endDate.replace(" ", "T"),
            }),
          ),
        Map(),
      );

      return store
        .set("holidays", holidaysMap)
        .set("loadingState", LOADING_STATE.SUCCEEDED);
    }

    case `${FETCH_HOLIDAYS}${ACTION_FAIL}`: {
      return store.set("loadingState", LOADING_STATE.FAILED);
    }

    case `${DELETE_HOLIDAY}${ACTION_SUCCESS}`: {
      const { id } = action.params;

      if (id) {
        return store.set(
          "holidays",
          store
            .get("holidays")
            .filter((item: Record<HolidayInterface>) => item.get("id") !== id),
        );
      }

      return store;
    }

    case INIT_PROCESSED_HOLIDAY: {
      return store.set("processedHoliday", initialHoliday());
    }

    case UPDATE_PROCESSED_HOLIDAY: {
      return store.mergeIn(["processedHoliday"], payload);
    }

    case CLEAR_PROCESSED_HOLIDAY: {
      return store.set("processedHoliday", null);
    }

    case `${FETCH_CONTRACTS_TO_PAUSE}${ACTION_SUCCESS}`: {
      const {
        data: { contractsToPause },
      } = payload;

      return store.set("contractsToPause", List(contractsToPause));
    }

    default: {
      return store;
    }
  }
};

export { holidaysReducer };
